import React, { useState, useEffect } from 'react';
import { analyticsService } from '../services/api';
import { AnalyticsData, DailyStats, MessageStatusStats, UserStats, TopTemplate } from '../types';
import LeadDetailsModal from './LeadDetailsModal';

const AnalyticsDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [statusStats, setStatusStats] = useState<MessageStatusStats[]>([]);
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [topTemplates, setTopTemplates] = useState<TopTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFilter, setModalFilter] = useState<{
    type: 'comprou' | 'status' | 'user';
    value: string | boolean;
    title: string;
  } | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, [dateFrom, dateTo]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const params = { dateFrom, dateTo };

      const [analyticsRes, dailyRes, statusRes, usersRes, templatesRes] = await Promise.all([
        analyticsService.getDashboard(params),
        analyticsService.getDailyStats(),
        analyticsService.getMessageStatusStats(params),
        analyticsService.getUserStats(params),
        analyticsService.getTopTemplates(params)
      ]);

      setAnalytics(analyticsRes.data);
      setDailyStats(dailyRes.data || []);
      setStatusStats(statusRes.data || []);
      setUserStats(usersRes.data || []);
      setTopTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      sent: 'text-blue-600',
      delivered: 'text-green-600',
      read: 'text-green-800',
      failed: 'text-red-600',
      pending: 'text-yellow-600'
    };
    return colors[status] || 'text-gray-600';
  };

  const getStatusBgColor = (status: string) => {
    const colors: { [key: string]: string } = {
      sent: 'bg-blue-100',
      delivered: 'bg-green-100',
      read: 'bg-green-200',
      failed: 'bg-red-100',
      pending: 'bg-yellow-100'
    };
    return colors[status] || 'bg-gray-100';
  };

  const handleCardClick = (type: 'comprou' | 'status' | 'user', value: string | boolean, title: string) => {
    setModalFilter({ type, value, title });
    setModalOpen(true);
  };

  const getStatusTitle = (status: string, count: number) => {
    const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1);
    return `Mensagens: ${formattedStatus} (${count})`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com Filtros */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">📊 Analytics WhatsApp</h2>
        <div className="flex space-x-4">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="Data inicial"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="Data final"
          />
          <button
            onClick={loadAnalytics}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            🔄 Atualizar
          </button>
        </div>
      </div>

      {/* Cards de Estatísticas Gerais */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div 
            onClick={() => handleCardClick('status', '', `Total Mensagens (${analytics.totalMessages})`)}
            className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-sm">📱</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Mensagens
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {analytics.totalMessages}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 text-sm">✅</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Taxa de Entrega
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {analytics.deliveryRate}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 text-sm">👁️</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Taxa de Leitura
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {analytics.readRate}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600 text-sm">🤖</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Automações Executadas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {analytics.automationsExecuted}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribuição por Status */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuição por Status</h3>
          <div className="space-y-3">
            {statusStats.map((stat) => (
              <div 
                key={stat.status} 
                onClick={() => handleCardClick('status', stat.status, getStatusTitle(stat.status, stat.count))}
                className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-all duration-200 hover:scale-[1.02]"
              >
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full ${getStatusBgColor(stat.status)} mr-3`}></div>
                  <span className={`text-sm font-medium ${getStatusColor(stat.status)}`}>
                    {stat.status.charAt(0).toUpperCase() + stat.status.slice(1)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{stat.count}</span>
                  <span className="text-sm text-gray-400">({stat.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Templates */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Templates Mais Usados</h3>
          <div className="space-y-3">
            {topTemplates.slice(0, 5).map((template, index) => (
              <div key={template.template_name} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-xs font-medium text-blue-600">{index + 1}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 truncate max-w-32">
                    {template.template_name}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{template.usage_count} usos</span>
                  <span className="text-sm text-green-600">({template.success_rate}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance por Usuário */}
      {userStats.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance por Usuário</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mensagens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entregues
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lidas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Leads
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Automações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userStats.map((user) => (
                  <tr key={user.user_email}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.user_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.total_messages}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {user.messages_delivered}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-800">
                      {user.messages_read}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                      {user.leads_contacted}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                      {user.automations_executed}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Gráfico Diário Simples */}
      {dailyStats.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Últimos 7 Dias</h3>
          <div className="space-y-2">
            {dailyStats.slice(0, 7).reverse().map((day) => (
              <div key={day.date_stats} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {new Date(day.date_stats).toLocaleDateString('pt-BR')}
                </span>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">{day.messages_sent} enviadas</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">{day.messages_delivered} entregues</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">{day.automations_executed} automações</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal para detalhes dos leads */}
      {modalOpen && modalFilter && (
        <LeadDetailsModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalFilter(null);
          }}
          filter={modalFilter}
        />
      )}
    </div>
  );
};

export default AnalyticsDashboard;